import { useEffect } from "react";

import {
    WithAnalyticsEventsProps,
    withAnalyticsEvents
} from "@atlaskit/analytics-next";

import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { useOneTrustContext } from "../../utils/onetrust-provider";
import { useAnalyticsClient } from "../wac-analytics/analytics-client";

import { AnalyticsData } from "./gasv3";
import { Initializer } from "./initializer";

export type InitializeAutoAtlAnalyticsProps = {
    readonly pageViewProperties?: AnalyticsData;
} & WithAnalyticsEventsProps;

const getBootstrapAnonymousId = (): string =>
    (typeof window !== "undefined" &&
        window.featureGateBootstrapValues?.identifiers?.analyticsAnonymousId) ||
    "";

const deepClone: <T extends object>(obj: T) => T = (obj) => {
    return JSON.parse(JSON.stringify(obj)) as typeof obj;
};

const InitializeAutoAtlAnalyticsImpl: React.FC<
    InitializeAutoAtlAnalyticsProps
> = ({
    pageViewProperties = {},
    createAnalyticsEvent
}: InitializeAutoAtlAnalyticsProps) => {
    const analyticsClient = useAnalyticsClient();
    const { analyticsOptOut, isConsentLoaded } = useOneTrustContext();
    const ANALYTICS_WRITE_KEY =
        (typeof window !== "undefined" &&
            (window as Window & { ANALYTICS_SOURCE_ID?: string })
                .ANALYTICS_SOURCE_ID) ||
        process.env["ANALYTICS_SOURCE_ID"] ||
        "";

    const isEnableOperationalEventWhenOptedOut = checkFeatureFlagGate(
        featureFlags.gwp10416DisableNonOperationalAnalyticsGateUip
    );

    useEffect(() => {
        if (isEnableOperationalEventWhenOptedOut) {
            if (!isConsentLoaded) {
                return;
            }
        } else {
            if (!analyticsClient || analyticsOptOut || !isConsentLoaded) {
                return;
            }
        }

        Initializer.initWithPageAnalytics(
            ANALYTICS_WRITE_KEY,
            deepClone(pageViewProperties),
            {},
            Date.now(),
            analyticsClient?.getAnonymousId() ?? getBootstrapAnonymousId(),
            createAnalyticsEvent || null,
            analyticsOptOut ?? true
        );
    }, [
        analyticsClient,
        analyticsOptOut,
        isConsentLoaded,
        ANALYTICS_WRITE_KEY,
        createAnalyticsEvent,
        pageViewProperties,
        isEnableOperationalEventWhenOptedOut
    ]);

    return null;
};

export const InitializeAutoAtlAnalytics = withAnalyticsEvents()(
    InitializeAutoAtlAnalyticsImpl
);
